"use client";

import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 10 10"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M5 2v6M2.5 5.5 5 8l2.5-2.5"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 10 10"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M5 8V2M2.5 4.5 5 2l2.5 2.5"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export type ListingPriceChangeBadgeProps = {
  priceDirection: "INCREASED" | "DECREASED" | "UNCHANGED" | null | undefined;
  priceChangePercent: number | null | undefined;
  /** False when the listing owner has turned public price history off. */
  visible?: boolean;
  compact?: boolean;
};

export function ListingPriceChangeBadge({
  priceDirection,
  priceChangePercent,
  visible = true,
  compact,
}: ListingPriceChangeBadgeProps) {
  const t = useTranslations();

  if (!visible) return null;
  if (!priceDirection || priceDirection === "UNCHANGED") return null;
  if (priceChangePercent == null || priceChangePercent === 0) return null;

  const absPercent = Math.abs(priceChangePercent);
  const isDropped = priceDirection === "DECREASED";
  const DirectionIcon = isDropped ? ArrowDownIcon : ArrowUpIcon;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none",
        isDropped
          ? "bg-emerald-50 text-emerald-700"
          : "bg-amber-50 text-amber-700",
      )}
    >
      <DirectionIcon className="size-2.5" />
      {compact
        ? `${absPercent}%`
        : isDropped
          ? t("saas.listingPreview.priceChange.dropped", {
              percent: absPercent,
            })
          : t("saas.listingPreview.priceChange.increased", {
              percent: absPercent,
            })}
    </span>
  );
}
